import {
  normalizeRegulationVersion,
  type AutoTaggingBundleTaskRegulation,
} from "../../../services/autoTaggingService";

export type AutoTaggingRegulationSource = {
  id?: string;
  regulation_id?: string;
  version?: string;
};

export function buildAutoTaggingRegulationsFromRows(
  rows: Array<AutoTaggingRegulationSource>,
): AutoTaggingBundleTaskRegulation[] {
  return rows
    .map((row) => {
      const regulationId = (row.regulation_id ?? row.id ?? "").trim();
      const version = normalizeRegulationVersion(row.version ?? "");
      if (!regulationId || !version) return null;
      return { regulation_id: regulationId, version };
    })
    .filter((item): item is AutoTaggingBundleTaskRegulation => item !== null);
}

/**
 * Resolve every selected regulation for bundle-task, including IDs that are
 * not on the current DataGrid page. Prefer the current/cached row; fetch
 * missing ones the same way Company Scope Impact does.
 */
export async function resolveAutoTaggingRegulationsForIds(
  selectedIds: readonly string[],
  getRow: (id: string) => AutoTaggingRegulationSource | null | undefined,
  fetchMissing?: (
    id: string,
  ) => Promise<AutoTaggingRegulationSource | null | undefined>,
): Promise<AutoTaggingBundleTaskRegulation[]> {
  const resolved = await Promise.all(
    selectedIds.map(async (id) => {
      const fromRow = buildAutoTaggingRegulationsFromRows([getRow(id) ?? {}])[0];
      if (fromRow) return fromRow;
      if (!fetchMissing) return null;
      try {
        const fetched = await fetchMissing(id);
        if (!fetched) return null;
        return (
          buildAutoTaggingRegulationsFromRows([
            {
              id,
              regulation_id: fetched.regulation_id ?? fetched.id ?? id,
              version: fetched.version,
            },
          ])[0] ?? null
        );
      } catch {
        return null;
      }
    }),
  );

  return resolved.filter(
    (item): item is AutoTaggingBundleTaskRegulation => item !== null,
  );
}
